import { CSSProperties, Dispatch, SetStateAction, useMemo } from "react";
import { Typography } from "antd";
import { ExtendedSectionHighlight } from "../types";
import { v4 as uuidv4 } from "uuid";
import { useCuepointsStore } from "../../animator/useCuepointsStore";
import { produce } from "immer";
import {
  parseScript,
  serializeScript,
  getAnchorRanges,
  stripOrphanHighlightAnchors,
} from "pages/media/helpers/scriptAnchors";
import TextSelector, { Selection } from "./TextSelector";
import { Storyboard } from "pages/media/types";
import { postToIframe } from "../../animator/utils";
import { useHighlightsStore } from "../../animator/useHighlightsStore";
import {
  charPosToWordIndex,
  wordIndexToCharEnd,
  wordIndexToCharPos,
} from "../helpers/helpers";
import { Marker } from "pages/media/helpers/types/";

export const SectionWithTextSelection = ({
  sectionContentWithHL,
  textStyle,
  handleScroll,
  sectionId,
  newSectionHighlight,
  setNewSectionHighlight,
  onSelectionChange,
  isRTL = false,
  language,
}: {
  sectionContentWithHL: string;
  textStyle?: CSSProperties;
  handleScroll: (id: string) => void;
  sectionId: string;
  newSectionHighlight?: ExtendedSectionHighlight;
  setNewSectionHighlight: Dispatch<
    SetStateAction<ExtendedSectionHighlight | undefined>
  >;
  onSelectionChange: (
    sectionId: string,
    updatedStoryboard: Storyboard
  ) => Promise<void>;
  isRTL?: boolean;
  language?: string;
}) => {
  const {
    sectionHighlights,
    setSectionHighlights,
    highlights,
    setHighlights,
    highlightedStoryboard,
    setHighlightedStoryboard,
  } = useHighlightsStore();

  const { addUpdateCount, cuepoints, setCuepoints } = useCuepointsStore();

  // Strip orphan HL anchors (ids not present in the sectionHighlights store)
  // before parsing so the editor never renders highlights without a matching
  // source. The raw storyboard in the store may still hold orphans until the
  // next save — the on-save reconciliation persists the cleanup.
  const cleanedContent = useMemo(() => {
    const validIds = new Set(sectionHighlights?.map((h) => h.id) ?? []);
    return stripOrphanHighlightAnchors(sectionContentWithHL ?? "", validIds);
  }, [sectionContentWithHL, sectionHighlights]);

  const parsed = useMemo(() => parseScript(cleanedContent), [cleanedContent]);

  const fullText = parsed.text;

  // Extract HL ranges and map to word indices for TextSelector
  const parsedContent = useMemo(() => {
    const hlRanges = getAnchorRanges(parsed, "HL");
    return hlRanges.map((range) => {
      const sectionHighlight = sectionHighlights?.find(
        (s) => s.id === range.id
      );
      const wordIndex = charPosToWordIndex(fullText, range.start, language);
      // Skip trailing spaces ("bar [[/HL]]baz"), or the end maps to the next word.
      const lastCharPos = range.start + range.text.trimEnd().length - 1;
      return {
        type: "highlight" as const,
        content: range.text,
        id: range.id,
        wordIndex,
        endWordIndex: Math.max(
          wordIndex,
          charPosToWordIndex(fullText, lastCharPos, language)
        ),
        selectedSourceId: sectionHighlight?.sourceId,
        generated: sectionHighlight?.generated,
      };
    });
  }, [parsed, fullText, sectionHighlights, language]);

  const handlePickSectionHighlight = (word: string, wordIndex: number) => {
    // If newSectionHighlight exists, it means the user has selected the word but hasn't created a highlight for it.
    // We allow to choose another word for it
    let newStoryboard = { ...highlightedStoryboard };
    if (newSectionHighlight) {
      const currentContentChunk = parsedContent.find(
        (chunk) => chunk.content === word && chunk.wordIndex === wordIndex
      );

      if (!currentContentChunk) {
        setNewSectionHighlight(undefined);
        setSectionHighlights([
          ...sectionHighlights.filter((s) => s.id !== newSectionHighlight.id),
        ]);

        // Remove the HL markers for the old highlight from the storyboard
        const newSections = produce(highlightedStoryboard.sections, (draft) => {
          const sectionToUpdate = draft.find(
            (s) => s.id === (newSectionHighlight.sectionId ?? sectionId)
          );
          if (sectionToUpdate) {
            const parsed = parseScript(sectionToUpdate.script.content);
            const cleaned = {
              text: parsed.text,
              markers: parsed.markers.filter(
                (m) => !(m.tag === "HL" && m.id === newSectionHighlight.id)
              ),
            };
            sectionToUpdate.script.content = serializeScript(cleaned);
          }
        });
        newStoryboard = { ...highlightedStoryboard, sections: newSections };
      } else {
        return;
      }
    }

    const sectionHighlight = {
      text: word,
      id: uuidv4(),
      sourceId: uuidv4(),
      sectionId,
      rank: 0,
      generated: false,
    };

    setNewSectionHighlight(sectionHighlight);
    setSectionHighlights([sectionHighlight, ...sectionHighlights]);

    // Insert HL markers at the word position
    const storyboardWithAddedHighlight = produce(newStoryboard, (draft) => {
      const section = draft.sections.find((s) => s.id === sectionId);
      if (!section) return;
      if (!section.script.content.includes(word)) return;

      const parsed = parseScript(section.script.content);
      const startPos = wordIndexToCharPos(parsed.text, wordIndex, language);
      const endPos = wordIndexToCharEnd(parsed.text, wordIndex, language);

      const newMarkers: Marker[] = [
        ...parsed.markers,
        {
          raw: `[[HL-${sectionHighlight.id}]]`,
          tag: "HL",
          id: sectionHighlight.id,
          position: startPos,
          closing: false,
        },
        {
          raw: `[[/HL-${sectionHighlight.id}]]`,
          tag: "HL",
          id: sectionHighlight.id,
          position: endPos,
          closing: true,
        },
      ];
      section.script.content = serializeScript({
        text: parsed.text,
        markers: newMarkers,
      });
    });

    setHighlightedStoryboard(storyboardWithAddedHighlight);
    postToIframe({
      type: "cuepoint-picked",
      id: sectionHighlight.id,
    });
  };

  const handleSelectionsChange = async (selections: Selection[]) => {
    if (!selections || selections.length === 0) return;

    // Parse the current section script to get text + all markers
    const sectionIndex = highlightedStoryboard?.sections?.findIndex(
      (s) => s.id === sectionId
    );
    if (sectionIndex === -1 || sectionIndex === undefined) return;

    const currentScript =
      highlightedStoryboard.sections[sectionIndex].script.content;
    const parsed = parseScript(currentScript ?? "");

    // Keep non-HL markers, rebuild HL markers from selections
    const nonHlMarkers = parsed.markers.filter((m) => m.tag !== "HL");
    const newHlMarkers: Marker[] = [];
    let processedSectionHighlights = [...sectionHighlights];

    parsedContent.forEach((chunk) => {
      const matchingSelection = selections.find(
        (selection) => selection.selectedSourceId === chunk.selectedSourceId
      );

      if (!matchingSelection) {
        processedSectionHighlights = processedSectionHighlights.filter(
          (sectionHighlight) =>
            sectionHighlight.sourceId !== chunk.selectedSourceId
        );
        return;
      }

      // Compute char positions from word indices
      const startPos = wordIndexToCharPos(
        parsed.text,
        matchingSelection.startIndex,
        language
      );
      const endPos = wordIndexToCharEnd(
        parsed.text,
        matchingSelection.endIndex,
        language
      );

      newHlMarkers.push(
        {
          raw: `[[HL-${chunk.id}]]`,
          tag: "HL",
          id: chunk.id,
          position: startPos,
          closing: false,
        },
        {
          raw: `[[/HL-${chunk.id}]]`,
          tag: "HL",
          id: chunk.id,
          position: endPos,
          closing: true,
        }
      );

      // Update text in the section highlight
      const text = parsed.text.slice(startPos, endPos);
      const currentSectionHighlightIndex =
        processedSectionHighlights?.findIndex(
          (sectionHighlight) =>
            sectionHighlight.id === matchingSelection.highlightId
        );

      if (currentSectionHighlightIndex !== -1) {
        processedSectionHighlights[currentSectionHighlightIndex] = produce(
          processedSectionHighlights[currentSectionHighlightIndex],
          (draft) => {
            draft.text = text;
          }
        );
      }
    });

    // Update newSectionHighlight text if it was part of the selections
    selections.forEach((selection) => {
      if (
        newSectionHighlight &&
        newSectionHighlight.sourceId === selection.selectedSourceId
      ) {
        setNewSectionHighlight((old) => ({ ...old, text: selection.text }));
      }
    });

    // Build updated script and storyboard
    const newScript = serializeScript({
      text: parsed.text,
      markers: [...nonHlMarkers, ...newHlMarkers],
    });

    const sections = highlightedStoryboard.sections.map((section, idx) =>
      idx === sectionIndex
        ? produce(section, (draft) => {
            draft.script.content = newScript;
          })
        : section
    );

    const updatedStoryboard = { ...highlightedStoryboard, sections };

    const filteredSectionHighlightsId = processedSectionHighlights.map(
      (sectionHighlight) => sectionHighlight.sourceId
    );

    const filteredHighlights = highlights.filter((highlight) =>
      filteredSectionHighlightsId.includes(highlight.id)
    );
    const filteredCuepoints = cuepoints.filter((cuepoint) =>
      filteredSectionHighlightsId.includes(cuepoint.sourceId)
    );

    setHighlights(filteredHighlights);
    setCuepoints(filteredCuepoints);
    setSectionHighlights(processedSectionHighlights);
    setHighlightedStoryboard(updatedStoryboard);
    addUpdateCount();

    await onSelectionChange?.(sectionId, updatedStoryboard);
  };

  const handleWordClick = (
    word: string,
    wordIndex: number,
    sourceId: string,
    selected: boolean
  ) => {
    return selected
      ? handleScroll(sourceId)
      : handlePickSectionHighlight(word, wordIndex);
  };

  return (
    <Typography.Text style={{ fontSize: 18, lineHeight: 1.8, ...textStyle }}>
      <TextSelector
        parsedContent={parsedContent}
        fullText={fullText}
        sectionId={sectionId}
        onSelectionsChange={(...args) => {
          return void handleSelectionsChange(...args);
        }}
        onWordClick={handleWordClick}
        isRTL={isRTL}
        language={language}
      />
    </Typography.Text>
  );
};
